import { useState } from "react";
import clsx from "clsx";
import type { GemStack } from "../types";
import { getGemIconPath, GEM_ICON_FALLBACK } from "../utils/assets";
import { AssetIcon } from "./AssetIcon";
import { AdminAssetPath } from "./AdminAssetPath";
import { formatGemLabel, gemStatKey, gemTypeColor, gemTypeSurface } from "./GemPickerModal";
import { ModalBackdrop } from "./ModalBackdrop";

interface GemDetailModalProps {
  gem: GemStack;
  heroClass?: string;
  sellGoldPerUnit?: number;
  actionLoading?: boolean;
  onClose: () => void;
  onSell?: (quantity: number) => void;
  onGoToBlacksmith?: () => void;
}

export function GemDetailModal({
  gem,
  heroClass,
  sellGoldPerUnit,
  actionLoading,
  onClose,
  onSell,
  onGoToBlacksmith,
}: GemDetailModalProps) {
  const [sellQty, setSellQty] = useState(1);
  const label = formatGemLabel(gem.gem_type, heroClass);
  const statKey = gemStatKey(gem.gem_type, heroClass);
  const statVal = gem.stats[statKey] ?? gem.stats[gem.gem_type] ?? Object.values(gem.stats)[0];
  const isLuck = gem.gem_type === "LUCK";
  const iconSrc = getGemIconPath(gem.gem_type, gem.level);
  const sellGold = sellGoldPerUnit ?? Math.round(120 * Math.pow(1.75, gem.level - 1));
  const canSell = Boolean(onSell) && sellGold > 0;

  return (
    <ModalBackdrop onClose={onClose}>
      <div className="w-full max-w-sm bg-kingdom-purple border border-kingdom-light rounded-2xl p-4 space-y-3">
        <div className="flex items-start gap-3">
          <div className="shrink-0 space-y-1">
            <div
              className={clsx(
                "w-16 h-16 rounded-xl border-2 flex items-center justify-center",
                gemTypeSurface(gem.gem_type, heroClass),
              )}
            >
              <AssetIcon
                src={iconSrc}
                fallback={GEM_ICON_FALLBACK}
                alt={label}
                className="w-12 h-12 object-contain"
                emojiClassName="text-3xl leading-none"
              />
            </div>
            <AdminAssetPath path={iconSrc} className="max-w-[4rem]" />
          </div>
          <div className="flex-1 min-w-0">
            <div className={clsx("font-bold text-lg", gemTypeColor(gem.gem_type, heroClass))}>
              {label} Lv.{gem.level}
            </div>
            <div className="text-xs text-kingdom-glow font-medium">
              Gem{gem.quantity > 1 ? ` ×${gem.quantity}` : ""}
            </div>
            {statVal != null && (
              <p className="text-sm text-emerald-400 mt-1">+{statVal}</p>
            )}
            {isLuck && (
              <p className="text-xs text-gray-400 mt-1">Used at the Blacksmith to boost gem synthesis.</p>
            )}
            {!isLuck && (
              <p className="text-xs text-gray-400 mt-1">Socket into equipment or combine at the Blacksmith.</p>
            )}
          </div>
          <button type="button" onClick={onClose} className="text-gray-400 text-xl">
            ✕
          </button>
        </div>

        <div className="flex flex-col gap-2 pt-1">
          {!isLuck && onGoToBlacksmith && (
            <button
              type="button"
              onClick={onGoToBlacksmith}
              disabled={actionLoading}
              className="w-full py-2.5 rounded-xl bg-amber-700 text-white font-bold text-sm disabled:opacity-50"
            >
              🔨 Go to Blacksmith
            </button>
          )}
          {canSell && (
            <>
              {gem.quantity > 1 && (
                <input
                  type="range"
                  min={1}
                  max={gem.quantity}
                  value={sellQty}
                  onChange={(e) => setSellQty(Number(e.target.value))}
                  className="w-full"
                />
              )}
              <button
                type="button"
                disabled={actionLoading}
                onClick={() => onSell?.(sellQty)}
                className="w-full py-2.5 rounded-xl border border-rose-500/50 bg-rose-950/40 text-rose-200 font-bold text-sm disabled:opacity-50"
              >
                Sell ×{sellQty} ({(sellGold * sellQty).toLocaleString()} gold)
              </button>
            </>
          )}
        </div>
      </div>
    </ModalBackdrop>
  );
}
